import { useEffect, useState } from 'react'
import { api, type Status } from '../lib/api'

/** What is built, what is not, and what is waiting on somebody.
 *
 *  This screen is the answer to "where are we up to", so it being out of date
 *  is worse than it not existing — it said step 4 was in progress and steps 5
 *  to 8 were still to come, long after all eight were live and in daily use.
 *  Keep it honest or delete it. */
const BUILD_ORDER = [
  { n: 1, what: 'App skeleton — config, migrations runner, Docker Compose, health', state: 'done' },
  { n: 2, what: 'Auth and tenancy — organizations, users, roles, sessions', state: 'done' },
  { n: 3, what: 'Settings lists — natures, departments, resolutions, relationships', state: 'done' },
  { n: 4, what: 'Members — registration with notification channels', state: 'done' },
  { n: 5, what: 'The call lifecycle — intake, broadcast, acceptance, close', state: 'done' },
  { n: 6, what: 'Dispatcher screen — built against the approved mockup', state: 'done' },
  { n: 7, what: 'Email broadcast — SMTP2GO, per-organization credentials', state: 'done' },
  { n: 8, what: 'AMI listener — VitalPBX caller-ID pop, tenant-filtered', state: 'done' },
] as const

/** Built after the numbered eight, because the customer needed them. */
const ALSO_BUILT = [
  'Super admin console — six connections, each with a test button',
  'Application-layer encryption for every stored credential',
  'Phone book — many numbers per caller, structured addresses',
  'Call log — answered, missed, who took it, who was never reached',
  'Caller card on the ring, with take-the-call',
  'Extension and inbound-number mapping, per organization',
] as const

/** Named here rather than left implied. A screen that lists only what works
 *  reads as a finished product. */
const NOT_YET = [
  { what: 'Escalation timers', note: 'settable on Operations and nothing reads them yet — the queue tints on fixed thresholds' },
  { what: 'Site notes', note: 'entry codes, a dog, where the gate is. The table and the addresses exist; nothing writes one' },
  { what: 'SMS', note: 'the adapter seam is built; no provider chosen' },
  { what: 'Map and address validation', note: 'no geocoding provider chosen' },
  { what: 'Radio recordings', note: 'nothing copies audio from the fleet server yet' },
  { what: 'Equipment', note: 'deferred by the plan' },
  { what: 'Reporting', note: 'deferred by the plan' },
] as const

const DEP_LABELS: Record<string, string> = {
  database: 'PostgreSQL',
  dispatch_panel: 'Dispatch panel',
  vitalpbx: 'VitalPBX',
  traccar: 'Traccar',
}

export function Overview() {
  const [status, setStatus] = useState<Status | null>(null)

  useEffect(() => {
    const load = () => api.status().then(setStatus).catch(() => setStatus(null))
    load()
    const t = setInterval(load, 15000)
    return () => clearInterval(t)
  }, [])

  return (
    <div className="workspace">
      <div className="card">
        <h2>Dependencies</h2>
        {status ? (
          Object.entries(status.dependencies).map(([key, dep]) => (
            <div className="dep" key={key}>
              <span
                className={`dot ${dep.ok === true ? 'ok' : dep.ok === false ? 'down' : 'idle'}`}
              />
              <span className="name">{DEP_LABELS[key] ?? key}</span>
              <span className="note">
                {dep.ok === true ? 'connected' : dep.ok === false ? 'unreachable' : dep.note}
              </span>
            </div>
          ))
        ) : (
          <div className="dep">
            <span className="dot down" />
            <span className="name">Cannot reach the server</span>
          </div>
        )}
        <p className="footnote">
          None of these may block this screen from loading. With all of them
          dark a dispatcher must still be able to type a number and dispatch.
        </p>
      </div>

      <div className="card">
        <h2>Build order</h2>
        {BUILD_ORDER.map((s) => (
          <div className={`step ${s.state}`} key={s.n}>
            <span className="n">{String(s.n).padStart(2, '0')}</span>
            <span className="what">{s.what}</span>
            <span className="tag">done</span>
          </div>
        ))}
        <p className="footnote">
          All eight are live. Built since, because they were needed:
          {' '}{ALSO_BUILT.join('; ')}.
        </p>
      </div>

      <div className="card">
        <h2>Not built yet</h2>
        <p className="blurb">
          Listed so this screen cannot read as a finished product. Several are
          waiting on a decision rather than on work.
        </p>
        {NOT_YET.map((s) => (
          <div className="listrow" key={s.what}>
            <span className="nm">{s.what}</span>
            <span className="dim">{s.note}</span>
          </div>
        ))}
      </div>

      {status?.schema?.applied && (
        <div className="card">
          <h2>Schema</h2>
          <div className="mono dim">
            {status.schema.applied.length} migration(s) applied
            {status.schema.pending?.length
              ? `, ${status.schema.pending.length} pending`
              : ', up to date'}
          </div>
        </div>
      )}
    </div>
  )
}
